import type {
  AIService,
  ChatMessage,
  ChatOptions,
  ChatRequest,
  ChatResponse,
  ChatStreamChunk,
} from '../../ports/ai'
import type { Usage } from '../../types'
import { createTransport, parseSse, type Transport } from '../http/transport'
import { normalizeBaseUrl, type LiteLlmConfig } from './config'

/** Subset of the OpenAI chat-completions wire format that we consume. */
interface WireUsage {
  prompt_tokens?: number
  completion_tokens?: number
}

interface WireDelta {
  content?: string | null
  /** LiteLLM normalizes provider reasoning traces into this field. */
  reasoning_content?: string | null
}

interface WireChunk {
  choices?: { delta?: WireDelta; finish_reason?: string | null }[]
  usage?: WireUsage | null
}

interface WireCompletion {
  choices?: { message?: WireDelta; finish_reason?: string | null }[]
  usage?: WireUsage | null
}

/**
 * AIService against a LiteLLM proxy. Chat is the only capability LiteLLM
 * provides for us — prompts, skills, RAG and speech stay on their own adapters.
 */
export class LiteLlmAIService implements AIService {
  private readonly t: Transport

  constructor(private readonly config: LiteLlmConfig) {
    this.t = createTransport({
      baseUrl: normalizeBaseUrl(config.baseUrl),
      getAccessToken: async () => config.virtualKey,
      fetch: config.fetch,
      timeoutMs: config.timeoutMs ?? 60_000,
      onError: config.onError,
    })
  }

  async chat(req: ChatRequest, opts?: ChatOptions): Promise<ChatResponse> {
    const res = await this.t.json<WireCompletion>('POST', 'v1/chat/completions', {
      body: this.body(req, false),
      signal: opts?.signal,
    })
    const choice = res.choices?.[0]
    return {
      message: { role: 'assistant', content: choice?.message?.content ?? '' },
      usage: mapUsage(res.usage),
      finishReason: mapFinishReason(choice?.finish_reason),
    }
  }

  async *stream(req: ChatRequest, opts?: ChatOptions): AsyncIterable<ChatStreamChunk> {
    const res = await this.t.request('POST', 'v1/chat/completions', {
      body: this.body(req, true),
      signal: opts?.signal,
      headers: { Accept: 'text/event-stream' },
    })

    let usage: Usage | undefined
    let finishReason: string | undefined

    for await (const data of parseSse(res)) {
      if (data === '[DONE]') break
      let chunk: WireChunk
      try {
        chunk = JSON.parse(data) as WireChunk
      } catch {
        // Keep-alives and comment frames are not JSON — ignore them.
        continue
      }
      if (chunk.usage) usage = mapUsage(chunk.usage)
      const choice = chunk.choices?.[0]
      if (choice?.finish_reason) finishReason = choice.finish_reason
      const reasoning = choice?.delta?.reasoning_content
      if (reasoning) yield { type: 'reasoning', text: reasoning }
      const text = choice?.delta?.content
      if (text) yield { type: 'delta', text }
    }

    yield { type: 'done', usage, finishReason }
  }

  /** Map a ChatRequest onto the OpenAI-compatible body LiteLLM expects. */
  private body(req: ChatRequest, stream: boolean): Record<string, unknown> {
    return {
      model: this.modelFor(req),
      messages: req.messages.map((m: ChatMessage) => ({ role: m.role, content: m.content })),
      ...(req.temperature !== undefined ? { temperature: req.temperature } : {}),
      ...(req.maxTokens !== undefined ? { max_tokens: req.maxTokens } : {}),
      ...(req.metadata ? { metadata: req.metadata } : {}),
      ...(stream ? { stream: true, stream_options: { include_usage: true } } : {}),
    }
  }

  /**
   * Thinking mode uses its own deployment when one is configured; otherwise the
   * normal model answers (LiteLLM rejects reasoning params on models that have
   * no reasoning support, so we never inject them blindly).
   */
  private modelFor(req: ChatRequest): string {
    if (req.model) return req.model
    if (req.mode === 'thinking' && this.config.thinkingModel) return this.config.thinkingModel
    return this.config.model
  }
}

/** AIService backed by a LiteLLM proxy. */
export function createLiteLlmAI(config: LiteLlmConfig): AIService {
  return new LiteLlmAIService(config)
}

function mapUsage(usage: WireUsage | null | undefined): Usage | undefined {
  if (!usage) return undefined
  return { inputTokens: usage.prompt_tokens, outputTokens: usage.completion_tokens }
}

function mapFinishReason(reason: string | null | undefined): ChatResponse['finishReason'] {
  switch (reason) {
    case 'stop':
      return 'stop'
    case 'length':
      return 'length'
    case 'tool_calls':
    case 'function_call':
      return 'tool'
    case 'content_filter':
      return 'content_filter'
    default:
      return undefined
  }
}
